import { call, put, select, takeLatest, takeEvery, takeLeading } from 'redux-saga/effects';
import * as lessonApi from '../api/lessonApi.js';
import { startAttempt, fetchAttempt, fetchInstructions, submitAnswer } from '../api/lessonApi.js';
import { fetchMyStepMediaSettings } from '../../courses/api/coursesApi.js';
import { courseMediaSettingsLoaded } from '../../courses/store/coursesListSlice.js';
import { extractMediaSettings } from '../../courses/lib/mediaSettings.js';
import {
  lessonInstructionsRequested,
  lessonInstructionsLoaded,
  lessonInstructionsFailed,
  lessonStartRequested,
  lessonStarted,
  lessonLoaded,
  lessonReviewLoaded,
  lessonAnswerSubmitting,
  lessonNextQuestion,
  lessonFeedbackReady,
  lessonFeedbackDismissed,
  lessonCompleted,
  lessonFailed,
  lessonCleared,
  lessonAnswerRejected,
  lessonMicGateRequired,
  lessonPreStartMicGateRequired,
  speechPrefsLoaded,
  preStartMicVerified,
  micCheckPassed,
  reportQuestionRequested,
  reportQuestionSucceeded,
  reportQuestionFailed,
  lessonVideoCheckRequested,
  upNextReceived,
  lessonCompletionContinueRequested,
} from './lessonSlice.js';
import { coursesRequested } from '../../courses/store/coursesListSlice.js';
import { courseDetailRequested } from '../../courses/store/courseDetailSlice.js';
import { toastShown } from '../../system/store/toastSlice.js';
import { captureSagaError } from '../../../lib/sentry.js';
import { getBlob, getRecording, clearBlob, clearAllBlobs } from '../../../lib/av/blobStore.js';
import { cleanupNativeRecordingFiles, nativeRecordingToBlob } from '../../../services/native/audioUploadService.js';
import { getNetworkStatus } from '../../../services/native/networkService.js';
import { getQuestionType, isAudioSubmitQuestion } from '../lib/questionModes.js';
import { isLessonAnswerComplete } from '../lib/answerCompleteness.js';
import { friendlyErrorMessage } from '../../../lib/errorCopy.js';
import { isLowBandwidth } from '../../../lib/network.js';
import { history } from '../../../lib/history.js';
import * as proctoringApi from '../../proctoring/api/proctoringApi.js';
import { matchProctorConfig, getProctorElementIds, isDesktopDevice } from '../../proctoring/lib/proctoringRules.js';
import * as proctorToken from '../../proctoring/lib/proctorToken.js';
import * as remoteProctoring from '../../proctoring/lib/remoteProctoring.js';
import { isProctorStoppedMessage, normalizeProctoringMessage } from '../../proctoring/lib/proctoringMessages.js';
import {
  proctorNotRequired,
  proctorRequired,
  proctorBlocked,
  proctorStarting,
  proctorStarted,
  proctorFailed,
} from '../../proctoring/store/proctorSlice.js';
import { buildCompletionModel, getCompletionContinueState, normalizeUpNext } from '../lib/completionModel.js';
import { clearPausedAttemptTimer, restorePausedAttemptTimer } from '../lib/pausedAttemptTimer.js';
import * as accountApi from '../../account/api/accountApi.js';
import { certificatesRequested, speechPrefsSynced } from '../../account/store/accountSlice.js';
import { featureVisibilityRequested } from '../../appVisibility/store/featureVisibilitySlice.js';

export function deriveReplayCount(questions) {
  return Array.isArray(questions) && questions.length > 1 ? 2 : null;
}

function linkedQuestionsFromData(data) {
  // if (Array.isArray(data?.questions)) return data.questions;
  if (Array.isArray(data?.next_question) && data.next_question.length > 1) return data.next_question;
  return [];
}

const LEGACY_TRIMMED_TEXT_TYPES = new Set(['SA', 'SASemantic', 'SAkeyword', 'LA']);
const OFFLINE_SUBMIT_MESSAGE = "You're offline. Reconnect and submit again.";
const AUDIO_PREPARATION_ERROR_CODE = 'AUDIO_UPLOAD_PREPARATION_FAILED';
const AUDIO_PREPARATION_ERROR_MESSAGE = 'Could not prepare your audio for upload. Please record your answer again.';

function audioPreparationError() {
  const error = new Error(AUDIO_PREPARATION_ERROR_MESSAGE);
  error.code = AUDIO_PREPARATION_ERROR_CODE;
  return error;
}

function isAudioPreparationError(err) {
  return err?.code === AUDIO_PREPARATION_ERROR_CODE;
}

// Recoverable submit error: backend returns 5xx + {status:"fail", message:"..."}
// when the user's answer can't be processed (e.g., unintelligible audio).
// Distinct from genuine auth/server-bug failures, which still go to lessonFailed.
function isRecoverableSubmitError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data;
  return (status === 500 || status === 422 || status === 400)
    && body?.status === 'fail'
    && typeof body?.message === 'string'
    && body.message.length > 0;
}

// Network failure: axios threw without receiving a response. Common for
// large multipart speaking-audio uploads that get dropped by an intermediary
// (Vite dev proxy timeout, flaky connection, server hung mid-recognize).
// Same recovery flow as a recoverable rejection — stay on the question,
// preserve the recording, surface a friendly message (offline-aware).

function isNetworkFailure(err) {
  return err && err.response === undefined;
}

function isServerSubmitFailure(err) {
  const status = err?.response?.status;
  return Number.isFinite(status) && status >= 500;
}

// Backend returns HTTP 404 + {status:"fail", message:"You have reached the
// max number of attempts for this element"} when the user tries to start an
// attempt past their cap. Surface as a friendly toast + nav-back instead of
// the generic lessonFailed path (issues.md #26, #35).
function isMaxAttemptsError(err) {
  const msg = err?.response?.data?.message ?? '';
  return /max number of attempts/i.test(msg);
}

function numericSeconds(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// The API is PHP-backed and returns numerics as strings in places, so numeric
// fields must be coerced rather than type-checked. `time_limit` was the one
// that was not: it reached the pre-lesson brief raw, failed a strict
// `typeof === 'number'` guard there, and silently fell through to a
// client-side estimate that undershot the real limit — a 60-minute cert test
// displayed as 54 (issues.md #37 retuned that estimate instead of fixing the
// cause). Unlike numericSeconds this keeps nullish as null: numericSeconds
// deliberately maps null to 0, so callers must guard nullish values when
// absence should mean there is no timer.
function numericMinutes(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function hasNextQuestion(data) {
  return Array.isArray(data?.next_question) && data.next_question.length > 0;
}

function buildQuestionPayload(data, options = {}) {
  const { attempt, next_question, answer_count, question_count, attempt_progress, element_name } = data;
  const q = Array.isArray(next_question) && next_question.length > 0 ? next_question[0] : null;
  const linkedQuestions = linkedQuestionsFromData(data);
  return {
    questionId: q?.question_id ?? null,
    questionNo: (answer_count ?? 0) + 1,
    questionCount: question_count ?? 0,
    attemptProgress: attempt_progress ?? 0,
    timeRemaining: options.timeRemaining ?? timedTimeRemaining(data),
    question: q,
    elementName: element_name ?? '',
    elementType: attempt?.element_type ?? '',
    isMultiQuestion: linkedQuestions.length > 1,
    questions: linkedQuestions,
    replayCount: deriveReplayCount(linkedQuestions),
    domains: attempt?.domains ?? '',
  };
}



function timedTimeRemaining(data) {
  const type = data?.attempt?.element_type;
  if (type === 'F') return numericSeconds(data?.time_remaining) ?? null;
  if (type === 'S') {
    return data?.time_remaining == null ? null : numericSeconds(data.time_remaining);
  }
  return null;
}


function restoredTimeRemaining(data, courseId, elementId) {
  const backendTimeRemaining = timedTimeRemaining(data);
  if (backendTimeRemaining === null) return null;
  return restorePausedAttemptTimer({
    attemptId: data?.attempt?.attempt_id,
    courseId,
    elementId,
    backendTimeRemaining,
  });
}

function readStoredProctorElementIds() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const parsed = JSON.parse(localStorage.getItem('proctorList') ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function buildCompletionPayload(data) {
  const { attempt, answers, element_name } = data;
  const completionModel = buildCompletionModel(data, {
    proctoredElementIds: readStoredProctorElementIds(),
  });
  return {
    elementName: element_name ?? '',
    elementType: attempt?.element_type ?? '',
    score: answers?.[0]?.score ?? null,
    oLevel: attempt?.o_level ?? null,
    oLevelRange: attempt?.o_level_range ?? null,
    attemptId: attempt?.attempt_id ?? null,
    upNext: completionModel.upNext,
    completionModel,
    // Plan H §4.7: CompletionScreen formats "time spent" from start_time.
    // The fixture supplies `attempt.start_time` (e.g. "2026-05-23T05:30:27");
    // `submit_time` is null in the fixture so we don't rely on it.
    attempt: attempt ? { start_time: attempt.start_time ?? null } : null,
  };
}

function normalizeReviewAnswers(answers) {
  if (Array.isArray(answers)) return answers;
  if (answers && typeof answers === 'object') return Object.values(answers);
  return [];
}

function buildReviewPayload(data, request) {
  const attempt = data?.attempt ?? {};
  const reviewAnswers = normalizeReviewAnswers(data?.answers);
  return {
    attemptId: attempt.attempt_id ?? request.reviewAttemptId ?? null,
    attemptName: attempt.attempt_name ?? '',
    courseId: data?.course_id ?? attempt.course_id ?? request.courseId,
    elementId: data?.element_id ?? attempt.element_id ?? request.elementId,
    elementName: data?.element_name ?? request.elementName ?? attempt.attempt_name ?? '',
    elementType: attempt.element_type ?? request.elementType ?? '',
    questionCount: data?.question_count ?? reviewAnswers.length,
    reviewAnswers,
  };
}

function audioExtension(mime = '') {
  const type = mime.toLowerCase();
  if (type.includes('mpeg') || type.includes('mp3')) return 'mp3';
  if (type.includes('ogg')) return 'ogg';
  if (type.includes('wav') || type.includes('wave')) return 'wav';
  if (type.includes('mp4') || type.includes('m4a') || type.includes('aac')) return 'm4a';
  return 'webm';
}

function appendWebAudioFiles(fd, ids) {
  ids.forEach((id) => {
    const blob = getBlob(id);
    if (blob) fd.append('audio[]', blob, `${id}.${audioExtension(blob.type)}`);
  });
}

function audioRecordingIds(answer) {
  return String(answer || '').split(',').filter(Boolean);
}

async function appendAudioFiles(fd, ids) {
  for (const id of ids) {
    const recording = getRecording(id);
    if (recording?.platform === 'native') {
      const blob = await nativeRecordingToBlob(recording);
      fd.append('audio[]', blob, recording.fileName || `${id}.${audioExtension(recording.mime)}`);
      continue;
    }
    const blob = getBlob(id);
    if (blob) fd.append('audio[]', blob, `${id}.${audioExtension(blob.type)}`);
  }
}

function buildAudioSubmitFormData(lesson, base) {
  const { currentAnswer, additionalAnswer } = lesson;
  const ids = audioRecordingIds(currentAnswer);
  const fd = new FormData();
  appendWebAudioFiles(fd, ids);
  fd.append('question_id', base.question_id);
  fd.append('user_answer', 'audio');
  fd.append('pause_time', base.pause_time);
  fd.append('attempt_id', base.attempt_id);
  fd.append('is_audio', 'yes');
  fd.append('is_retry', 0);
  fd.append('additional_user_answer', additionalAnswer || '');
  fd.append('skip_domain', 0);
  return fd;
}

export function buildSubmitPayload(lesson) {
  const { question, currentAnswer, additionalAnswer, isMultiQuestion, attemptId, timeRemaining } = lesson;
  const base = {
    question_id: question?.question_id,
    pause_time: timeRemaining ?? 0,
    attempt_id: attemptId,
    skip_domain: 0,
  };

  if (isMultiQuestion) {
    return {
      ...base,
      user_answer: currentAnswer,
      is_audio: 'no',
      is_linked_ques: 1,
    };
  }

  if (isAudioSubmitQuestion(question)) {
    return buildAudioSubmitFormData(lesson, base);
  }

  const type = getQuestionType(question);
  // const userAnswer = LEGACY_TRIMMED_TEXT_TYPES.has(type)
  //   ? String(currentAnswer ?? '').trim()
  //   : currentAnswer;
  return { ...base, user_answer: currentAnswer, is_audio: 'no' };
}

export async function buildSubmitPayloadForSubmit(lesson) {
  if (!isAudioSubmitQuestion(lesson.question)) return buildSubmitPayload(lesson);
  const payload = buildSubmitPayload(lesson);
  payload.delete('audio[]');
  const ids = audioRecordingIds(lesson.currentAnswer);
  await appendAudioFiles(payload, ids);
  const audioFiles = payload.getAll('audio[]');
  if (
    ids.length === 0
    || audioFiles.length !== ids.length
    || audioFiles.some((file) => !file || file.size === 0)
  ) {
    throw audioPreparationError();
  }
  return payload;
}

function getSubmittedAudioRecords(lesson) {
  if (!isAudioSubmitQuestion(lesson.question)) return [];
  return audioRecordingIds(lesson.currentAnswer)
    .map((id) => ({ id, recording: getRecording(id) }))
    .filter(({ recording }) => recording);
}

async function cleanupSubmittedAudio(records) {
  const nativeRecordings = records
    .map(({ recording }) => recording)
    .filter((recording) => recording?.platform === 'native' && recording?.filePath);
  await cleanupNativeRecordingFiles(nativeRecordings);
  records.forEach(({ id }) => clearBlob(id));
}

async function isOfflineForSubmit() {
  try {
    const status = await getNetworkStatus();
    return status?.connected === false;
  } catch {
    return false;
  }
}

function withSubmittedSnapshot(feedbackData, lesson) {
  if (!feedbackData) return null;
  return {
    ...feedbackData,
    submittedQuestion: lesson.question,
    submittedAnswer: lesson.currentAnswer,
  };
}

function feedbackDataForResponse(data, lesson, { includeSubmittedSnapshot = false } = {}) {
  const feedbackData = data?.answers?.[0] ?? null;
  return includeSubmittedSnapshot ? withSubmittedSnapshot(feedbackData, lesson) : feedbackData;
}

function buildFeedbackPayload(data, lesson, options = {}) {
  const linkedQuestions = linkedQuestionsFromData(data);
  const hasNext = hasNextQuestion(data);
  return {
    feedbackData: feedbackDataForResponse(data, lesson, options),
    questionNo: (data.answer_count ?? 0) + 1,
    questionCount: data.question_count ?? 0,
    attemptProgress: data.attempt_progress ?? 0,
    timeRemaining: timedTimeRemaining(data),
    nextQuestion: hasNext ? data.next_question[0] : null,
    pendingCompletion: hasNext ? null : buildCompletionPayload(data),
    replayCount: deriveReplayCount(linkedQuestions),
    isMultiQuestion: linkedQuestions.length > 1,
    questions: linkedQuestions,
  };
}

function* emitAttemptAdvance(res, lesson, options = {}) { 
  if (!res?.status) {
    yield put(lessonFailed(res?.message || options.failureMessage || 'Failed to submit answer'));
    return;
  }

  // if (res.code === 204) {
  //   clearPausedAttemptTimer(lesson?.attemptId);
  //   yield put(lessonCompleted(buildCompletionPayload(res.data)));
  //   return;
  // }

  if (res.code !== 200) return;

  clearPausedAttemptTimer(lesson?.attemptId ?? res.data?.attempt?.attempt_id);
  const showFeedback = res.data.show_feedback?.show_feedback === 'Y';
  const hasNext = hasNextQuestion(res.data);

  if (!hasNext) {
    if (showFeedback) {
      yield put(lessonFeedbackReady(buildFeedbackPayload(res.data, lesson, options)));
    } else {
      yield put(lessonCompleted(buildCompletionPayload(res.data)));
    }
    return;
  }

  if (showFeedback) {
    yield put(lessonFeedbackReady(buildFeedbackPayload(res.data, lesson, options)));
    return;
  }

  yield put(lessonNextQuestion(buildQuestionPayload(res.data)));
}

function isAdaptiveElementType(type) {
  return type === 'A';
}

function shouldPreflightInstructions(type, needsInstructionPreflight) {
  return needsInstructionPreflight || isAdaptiveElementType(type);
}

function getContinueAttemptId(instructionData) {
  const id = instructionData?.continue_attempt_id;
  return id && Number(id) !== 0 ? id : null;
}

function getResolvedInstructionElementId(instructionData) {
  const id = instructionData?.element_id;
  return id && Number(id) !== 0 ? String(id) : null;
}

function getFirstReviewAttemptId(instructionData) {
  const attempts = instructionData?.review_attempts;
  if (Array.isArray(attempts)) return attempts[0]?.attempt_id ?? attempts[0] ?? null;
  if (attempts && typeof attempts === 'object') {
    const first = Object.values(attempts)[0];
    return first?.attempt_id ?? first ?? null;
  }
  return instructionData?.review_attempt_id ?? null;
}

function selectOrgId(state) {
  return state.auth?.org?.id
    ?? state.auth?.user?.user_orgid
    ?? state.auth?.user?.org_id
    ?? localStorage.getItem('orgID')
    ?? null;
}

function selectCandidateName(state) {
  const user = state.auth?.user ?? {};
  return user.login_id
    ?? user.email
    ?? user.mobile
    ?? user.phone
    ?? user.user_id
    ?? localStorage.getItem('loginId')
    ?? localStorage.getItem('login_user_id')
    ?? 'candidate';
}

function* ensureCourseMediaSettings(courseId) {
  let cachedStatus = null;
  try {
    cachedStatus = yield select((s) => s.coursesList?.media?.jwplayerStatus);
  } catch {
    cachedStatus = null;
  }
  if (typeof cachedStatus === 'boolean') return;

  try {
    const res = yield call(fetchMyStepMediaSettings);
    if (res && res.status && res.code === 200) {
      const mediaSettings = extractMediaSettings(res);
      if (mediaSettings) yield put(courseMediaSettingsLoaded(mediaSettings));
    }
  } catch (err) {
    captureSagaError(err, { action: 'lesson/ensureMyStepMediaSettings', courseId });
  }
}

function normalizeInstructionDomains(data, fallback = '') {
  const domains = data?.domains;
  if (!Array.isArray(domains)) return data?.element_domain ?? fallback ?? '';
  const map = {
    listening: 'L',
    speaking: 'S',
    reading: 'R',
    writing: 'W',
  };
  return domains
    .map((domain) => map[String(domain).toLowerCase()] ?? String(domain).charAt(0).toUpperCase())
    .join('');
}

function buildInstructionPayload(data, request) {
  return {
    courseId: String(data?.course_id ?? request.courseId),
    elementId: request.elementId,
    resolvedElementId: getResolvedInstructionElementId(data),
    elementName: data?.element_name ?? request.elementName ?? '',
    elementType: data?.element_type ?? request.elementType ?? '',
    domains: normalizeInstructionDomains(data, request.elementDomain),
    questionCount: data?.number_of_questions ?? 0,
    timeLimit: numericMinutes(data?.time_limit),
    continueAttemptId: getContinueAttemptId(data),
    hideStart: data?.hide_start ?? 0,
    attemptRemaining: request.attemptRemaining ?? 0,
    reviewAttemptId: getFirstReviewAttemptId(data),
  };
}

function hasSpeechDomain(domains) {
  return String(domains ?? '').includes('S');
}

function speechStatusEnabled(res) {
  return !!res?.status && res.code === 200 && !!res.data?.speech_is_on;
}

function* requirePreStartMicIfNeeded(startPayload, domains) {
  if (startPayload.skipPreStartMicGate || !hasSpeechDomain(domains)) return false;

  const speechRes = yield call(accountApi.fetchSpeechPrefs);
  if (speechStatusEnabled(speechRes)) return false;

  yield put(lessonPreStartMicGateRequired({
    ...startPayload,
    skipPreStartMicGate: true,
  }));
  return true;
}

function* startAttemptForElement(courseId, elementId, { fallbackElementId = null } = {}) {
  try {
    return yield call(startAttempt, courseId, elementId);
  } catch (err) {
    if (isMaxAttemptsError(err) || !fallbackElementId || String(fallbackElementId) === String(elementId)) {
      throw err;
    }
    return yield call(startAttempt, courseId, fallbackElementId);
  }
}

function* checkProctorRequirement({ courseId, elementId, resolvedElementId }) {
  try {
    const orgId = yield select(selectOrgId);
    const list = yield call(proctoringApi.fetchProctorDetails, { courseId, orgId });
    const items = Array.isArray(list) ? list : list?.data ?? [];
    localStorage.setItem('proctorList', JSON.stringify(getProctorElementIds(items)));
    const match = matchProctorConfig(items, { courseId, elementId, resolvedElementId, orgId });
    return { match, orgId };
  } catch (err) {
    captureSagaError(err, { action: 'lesson/checkProctorRequirement', courseId, elementId });
    return { match: null, orgId: null, checkFailed: true };
  }
}

function* prepareProctoring({ courseId, elementId, resolvedElementId }) {
  const { match } = yield call(checkProctorRequirement, { courseId, elementId, resolvedElementId });
  if (!match) {
    yield put(proctorNotRequired({ courseId, elementId, resolvedElementId }));
    return { required: false };
  }

  const eventName = match.proctor_event_name;
  yield put(proctorRequired({
    courseId,
    elementId,
    resolvedElementId,
    eventName,
    matchedElementId: match.element_id,
  }));

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (!isDesktopDevice(ua)) {
    const message = 'This test is not allowed to be taken on a mobile phone. Please use a desktop or laptop.';
    yield put(proctorBlocked(message));
    yield put(lessonFailed(message));
    return { required: true, blocked: true };
  }

  return {
    required: true,
    courseId,
    elementId,
    resolvedElementId,
    eventName,
    matchedElementId: match.element_id,
  };
}

function* launchPreparedProctoring(context) {
  if (!context?.required || context.blocked) return true;

  try {
    yield put(proctorStarting());
    const candidateName = yield select(selectCandidateName);
    const token = yield call(proctorToken.getRemoteProctorToken, {
      courseId: context.courseId,
      elementId: context.elementId,
      eventName: context.eventName,
      candidateName,
      candidateId: `${candidateName}-${context.courseId}-${context.elementId}`,
    });
    yield call(remoteProctoring.startRemoteProctoring, token);
    localStorage.setItem('proctorCourseId', String(context.courseId));
    localStorage.setItem('active_course_id', String(context.courseId));
    localStorage.setItem('active_element_id', String(context.elementId));
    yield put(proctorStarted());
    return true;
  } catch (err) {
    const message = normalizeProctoringMessage(err) || 'Could not start remote proctoring. Please try again.';
    captureSagaError(err, {
      action: 'lesson/startRemoteProctoring',
      courseId: context.courseId,
      elementId: context.elementId,
    });
    yield put(proctorFailed(message));
    if (isProctorStoppedMessage(message)) {
      yield put(toastShown({
        variant: 'warning',
        message,
      }));
    }
    yield put(lessonFailed(message));
    return false;
  }
}

export function* handleLessonInstructions(action) {
  try {
    const { courseId, elementId } = action.payload;
    const instructionRes = yield call(fetchInstructions, courseId, elementId);
    if (!instructionRes?.status || instructionRes.code !== 200) {
      yield put(lessonInstructionsFailed(
        instructionRes?.message || 'Failed to load lesson instructions',
      ));
      return;
    }
    const payload = buildInstructionPayload(instructionRes.data, action.payload);

    // Keep the brief loading until the mic preference is known. Marking the
    // instructions ready first briefly shows the mic step, then replaces it
    // with the summary when checkspeech reports speech_is_on: true.
    if (hasSpeechDomain(payload.domains)) {
      try {
        const speechRes = yield call(accountApi.fetchSpeechPrefs);
        yield put(speechPrefsLoaded(speechStatusEnabled(speechRes)));
      } catch {
        yield put(speechPrefsLoaded(null));
      }
    }
    yield put(lessonInstructionsLoaded(payload));
  } catch (err) {
    captureSagaError(err, { action: 'lesson/lessonInstructionsRequested' });
    yield put(lessonInstructionsFailed(err?.message || 'Failed to load lesson instructions'));
  }
}

export function* handleLessonStart(action) {
  try {
    clearAllBlobs();
    const {
      courseId,
      elementId,
      elementType,
      needsInstructionPreflight,
      continueAttemptId,
      forceNewAttempt,
      reviewAttemptId,
      resolvedElementId,
      instructionPreflightComplete,
      elementDomain,
    } = action.payload;
    let data = null;
    let fallbackElementId = resolvedElementId ?? null;
    let proctorContext = null;

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('active_course_id', String(courseId));
      localStorage.setItem('active_element_id', String(elementId));
    }

    yield call(ensureCourseMediaSettings, courseId);


    // reviewAttemptId is set when the user clicks "Review" on a completed lesson. In that case, we fetch the review attempt and load it directly, skipping any preflight or proctoring steps.
    if (reviewAttemptId) {
      const reviewRes = yield call(fetchAttempt, reviewAttemptId, { review: true });
      if (!reviewRes?.status || reviewRes.code !== 200) {
        yield put(lessonFailed(reviewRes?.message || 'Failed to load review'));
        return;
      }
      yield put(lessonReviewLoaded(buildReviewPayload(reviewRes.data, action.payload)));
      return;
    }

    // If continueAttemptId is set, we are resuming a previously started attempt. In that case, we fetch the attempt and load it directly, skipping any preflight or proctoring steps.  
    if (continueAttemptId) {
      proctorContext = yield call(prepareProctoring, {
        courseId,
        elementId,
        resolvedElementId: fallbackElementId,
      });
      if (proctorContext.blocked) return;

      const resumedRes = yield call(fetchAttempt, continueAttemptId);
      if (!resumedRes?.status || resumedRes.code !== 200) {
        yield put(lessonFailed(resumedRes?.message || 'Failed to resume lesson'));
        return;
      }
      const proctorReady = yield call(launchPreparedProctoring, proctorContext);
      if (!proctorReady) return;
      if (resumedRes.data?.attempt) {
        yield put(lessonStarted(resumedRes.data.attempt));
      }
      data = resumedRes.data;
    }

    // If we don't have data yet, and the element type requires instruction preflight, we fetch the instructions first. If the instructions indicate that we should continue a previous attempt, we fetch that attempt. Otherwise, we may need to start a new attempt after preflight.
    const preflightInstructions = !instructionPreflightComplete
      && shouldPreflightInstructions(elementType, needsInstructionPreflight);
    if (!data && preflightInstructions) {
      const instructionRes = yield call(fetchInstructions, courseId, elementId);
      if (!instructionRes?.status || instructionRes.code !== 200) {
        yield put(lessonFailed(instructionRes?.message || 'Failed to load lesson instructions'));
        return;
      }

      // Legacy treats the adaptive row's elementId as a launcher. It calls
      // fetchinstructions first; when the response has continue_attempt_id it
      // resumes that attempt directly. Some adaptive launchers accept
      // startAttempt on the launcher id; others require the resolved
      // data.element_id. Preserve the legacy launcher-first path, but keep the
      // resolved id as a fallback so the learner does not land on a false
      // first-load error.
      fallbackElementId = getResolvedInstructionElementId(instructionRes.data);
      const instructionContinueAttemptId = getContinueAttemptId(instructionRes.data);
      if (instructionContinueAttemptId && !forceNewAttempt) {
        proctorContext = yield call(prepareProctoring, {
          courseId,
          elementId,
          resolvedElementId: fallbackElementId,
        });
        if (proctorContext.blocked) return;

        const resumedRes = yield call(fetchAttempt, instructionContinueAttemptId);
        if (!resumedRes?.status || resumedRes.code !== 200) {
          yield put(lessonFailed(resumedRes?.message || 'Failed to resume lesson'));
          return;
        }
        const proctorReady = yield call(launchPreparedProctoring, proctorContext);
        if (!proctorReady) return;
        if (resumedRes.data?.attempt) {
          yield put(lessonStarted(resumedRes.data.attempt));
        }
        data = resumedRes.data;
      }

      if (!data) {
        const instructionDomains = normalizeInstructionDomains(instructionRes.data, elementDomain);
        const gated = yield call(requirePreStartMicIfNeeded, {
          ...action.payload,
          resolvedElementId: fallbackElementId,
          instructionPreflightComplete: true,
        }, instructionDomains);
        if (gated) return;
      }
    } else if (!data && instructionPreflightComplete) {
      const instructionDomains = yield select((s) => s.lesson?.instructionDomains ?? elementDomain ?? '');
      const gated = yield call(requirePreStartMicIfNeeded, action.payload, instructionDomains);
      if (gated) return;
    }

    // If we still don't have data, we need to start a new attempt. We prepare proctoring if needed, then call startAttemptForElement. If the start attempt fails and we have a fallback element id, we try starting an attempt on the fallback. If that succeeds, we fetch the attempt data and proceed.

    if (!data) {
      proctorContext = yield call(prepareProctoring, {
        courseId,
        elementId,
        resolvedElementId: fallbackElementId,
      });
      if (proctorContext.blocked) return;

      let startRes = yield call(startAttemptForElement, courseId, elementId, {
        fallbackElementId: preflightInstructions ? fallbackElementId : null,
      });
      if (
        preflightInstructions
        && (!startRes?.status || startRes.code !== 200)
        && fallbackElementId
        && String(fallbackElementId) !== String(elementId)
      ) {
        startRes = yield call(startAttempt, courseId, fallbackElementId);
      }
      if (!startRes?.status || startRes.code !== 200) {
        yield put(lessonFailed(startRes?.message || 'Failed to start lesson'));
        return;
      }
      const proctorReady = yield call(launchPreparedProctoring, proctorContext);
      if (!proctorReady) return;
      yield put(lessonStarted(startRes.data.attempt));

      const fetchRes = yield call(fetchAttempt, startRes.data.attempt.attempt_id);
      if (!fetchRes?.status || fetchRes.code !== 200) {
        yield put(lessonFailed(fetchRes?.message || 'Failed to load lesson'));
        return;
      }
      data = fetchRes.data;
    }

    // Restore the whole-test countdown saved when the learner left the attempt.
    const timeRemaining = restoredTimeRemaining(data, courseId, elementId);
    yield put(lessonLoaded(buildQuestionPayload(data, { timeRemaining })));



    // Resumed-attempt path: when fetchAttempt returns show_feedback: 'Y',
    // the user was suspended on the feedback screen for their last answer.
    // answers[0] is that feedback; next_question[0] (if any) is what they'll
    // see when they click Next. Layer feedback state on top of the loaded
    // question so the player picks up exactly where they left off. --- this is for Formative alone for now
    const showFeedback = data.show_feedback?.show_feedback === 'Y';
    const hasFeedbackData = Array.isArray(data.answers) && data.answers.length > 0;
    if (showFeedback && hasFeedbackData) {
      const hasNext = Array.isArray(data.next_question) && data.next_question.length > 0;
      yield put(lessonFeedbackReady({
        feedbackData: data.answers[0],
        questionNo: (data.answer_count ?? 0) + 1,
        questionCount: data.question_count ?? 0,
        attemptProgress: data.attempt_progress ?? 0,
        timeRemaining,
        nextQuestion: hasNext ? data.next_question[0] : null,
        pendingCompletion: hasNext ? null : buildCompletionPayload(data),
        replayCount: deriveReplayCount(data.questions),
        isMultiQuestion: Array.isArray(data.questions) && data.questions.length > 1,
        questions: data.questions ?? [],
      }));
    }

    // Mic check (not browser mic permission but the one we call /checkspeech api)
    if (typeof window !== 'undefined') {
      const needsMic = (data?.attempt?.domains ?? '').includes('S'); // domain here is a string like "LRSW" or "S" or "R", so we just check for the presence of "S"
      const checked = window.sessionStorage.getItem('step:mic-checked-session') === '1';
      if (needsMic && !checked) {
        yield put(lessonMicGateRequired());
      }
    }
  } catch (err) {
    if (isMaxAttemptsError(err)) {
      const { courseId } = action.payload;
      yield put(toastShown({
        variant: 'warning',
        message: "You've reached the maximum attempts for this lesson.",
      }));
      history.replace(`/courses/${courseId}`);
      return;
    }
    captureSagaError(err, { action: 'lesson/lessonStartRequested' });
    yield put(lessonFailed(err?.message || 'Failed to start lesson'));
  }
}

// for submitanswer api
export function* handleLessonSubmit() {
  try {
    const lesson = yield select((s) => s.lesson);
    console.log(lesson, "submitAnswer")
    if (!isLessonAnswerComplete(lesson)) { // ok this is defensive programming
      yield put(lessonAnswerRejected({
        message: isAudioSubmitQuestion(lesson.question)
          ? 'Please record a complete spoken response before submitting.'
          : 'Please answer the question before submitting.',
      }));
      return;
    }

    const isOffline = yield call(isOfflineForSubmit);
    if (isOffline) {
      yield put(lessonAnswerRejected({ message: OFFLINE_SUBMIT_MESSAGE }));
      return;
    }

    const submittedAudioRecords = getSubmittedAudioRecords(lesson);
    const payload = yield call(buildSubmitPayloadForSubmit, lesson);
    const res = yield call(submitAnswer, payload);

    if (res?.status && (res.code === 204 || res.code === 200)) {
      yield call(cleanupSubmittedAudio, submittedAudioRecords);
    }

    yield call(emitAttemptAdvance, res, lesson, {
      includeSubmittedSnapshot: true,
      failureMessage: 'Failed to submit answer',
    });
  } catch (err) {
    if (isAudioPreparationError(err)) {
      yield put(lessonAnswerRejected({ message: err.message }));
      return;
    }
    if (isRecoverableSubmitError(err)) {
      yield put(lessonAnswerRejected({ message: err.response.data.message }));
      return;
    }
    if (isNetworkFailure(err)) {
      yield put(lessonAnswerRejected({ message: friendlyErrorMessage(err, "Could not reach the server. Check your connection and try again.") }));
      return;
    }
    if (isServerSubmitFailure(err)) {
      yield put(lessonAnswerRejected({ message: 'Could not submit your answer. Please try again.' }));
      return;
    }
    captureSagaError(err, { action: 'lesson/lessonAnswerSubmitting' });
    yield put(lessonFailed(err?.message || 'Failed to submit answer'));
  }
}

export function* handlePreStartMicVerified() {
  // Mirror of handleMicCheckPassed's persistence, for the brief's own mic step.
  // Failure is non-blocking: the check already succeeded locally, so the user
  // continues; the flag simply is not remembered server-side this time.
  try {
    const speechRes = yield call(accountApi.updateSpeechPrefs, true);
    if (speechRes?.status && speechRes.code === 200) {
      yield put(speechPrefsSynced(true));
    }
  } catch (err) {
    captureSagaError(err, { action: 'lesson/preStartMicVerified' });
  }
}

export function* handleMicCheckPassed() {
  const pendingStart = yield select((s) => s.lesson?.pendingStartAfterMic ?? null);
  let saved = false;
  try {
    const speechRes = yield call(accountApi.updateSpeechPrefs, true);
    if (pendingStart && !speechStatusEnabled(speechRes)) {
      yield put(toastShown({
        variant: 'error',
        message: speechRes?.message || 'Could not save microphone status. Please try again.',
      }));
      yield put(lessonPreStartMicGateRequired(pendingStart));
      return;
    }
    saved = !!speechRes?.status && speechRes.code === 200;
  } catch (err) {
    if (pendingStart) {
      captureSagaError(err, { action: 'lesson/micCheckPassed' });
      yield put(toastShown({
        variant: 'error',
        message: 'Could not save microphone status. Please try again.',
      }));
      yield put(lessonPreStartMicGateRequired(pendingStart));
    }
    return;
  }

  if (saved) yield put(speechPrefsSynced(true));
  if (pendingStart) {
    yield put(lessonStartRequested({
      ...pendingStart,
      skipPreStartMicGate: true,
    }));
  }
}

// Exported for testability — the watcher below dispatches to this function.
// Fires on any action that moves the player past the current question. The
// slice already clears audioRecordings metadata in its reducers; this drops
// the actual Blob bytes from the module-level store so they don't accumulate
// across an attempt (a 5-question element with re-records was holding
// multiple MB of WAV in memory after the slice had forgotten about it).
export function handleLessonClearedSideEffect() {
  clearAllBlobs();
}

// In-test "Report this question" modal -> POST /v1/questionFeedback.
// Legacy reloaded the page on success; the new app shows a toast and lets
// the user continue. The category is pipe-joined with the optional free-text
// note to match what the backend expects (single `feedback` field).
export function* handleReportQuestion(action) {
  const { category, note } = action.payload;
  const attemptId = yield select((s) => s.lesson.attemptId);
  const questionId = yield select((s) => s.lesson.questionId);
  const feedback = note ? `${category}|${note}` : category;
  try {
    const res = yield call(lessonApi.reportQuestion, {
      attempt_id: attemptId,
      question_id: questionId,
      feedback,
    });
    if (res && res.status && res.code === 200) {
      yield put(reportQuestionSucceeded());
      yield put(toastShown({ variant: 'success', message: 'Thanks — we got it.' }));
    } else {
      const msg = res?.message || 'Could not send report';
      yield put(reportQuestionFailed(msg));
      yield put(toastShown({ variant: 'error', message: msg }));
    }
  } catch (err) {
    captureSagaError(err, { action: action.type });
    const friendly = friendlyErrorMessage(err);
    yield put(reportQuestionFailed(friendly));
    yield put(toastShown({ variant: 'error', message: friendly }));
  }
}

export function* handleLessonVideoCheck() {
  try {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      yield put(toastShown({ variant: 'warning', message: 'You appear to be offline. Check your connection and try again.' }));
      return;
    }
    if (isLowBandwidth()) {
      yield put(toastShown({ variant: 'warning', message: 'Your internet speed is low. The video may take a moment.' }));
      return;
    }

    const attemptId = yield select((s) => s.lesson.attemptId);
    const questionId = yield select((s) => s.lesson.questionId);
    const videoURL = yield select((s) => {
      const q = s.lesson.isMultiQuestion ? s.lesson.questions?.[0] : s.lesson.question;
      return q?.source_youtube_video ?? q?.youtube_video_id ?? q?.jw_player_id ?? '';
    });

    const res = yield call(lessonApi.checkVideo, {
      attempt_id: attemptId,
      question_id: questionId,
      videoURL,
    });

    if (res?.status && res.code === 204) {
      window.location.reload();
      return;
    }

    yield put(toastShown({
      variant: 'error',
      message: res?.message || 'The video is still not loading. Please try again.',
    }));
  } catch (err) {
    captureSagaError(err, { action: 'lesson/lessonVideoCheckRequested' });
    yield put(toastShown({ variant: 'error', message: friendlyErrorMessage(err, 'Could not check the video right now.') }));
  }
}

// Fires when the lesson finishes (code 204 / pendingCompletion resolves).
// CompletionScreen may show a next card only when the completion response
// itself includes `upnext`; the global dashboard `/up-next` feed can point to
// unrelated certification/proctoring items and is intentionally not used here.
export function* handleCompletionMounted() {
  try {
    const lesson = yield select((s) => s.lesson ?? {});
    const existing = lesson?.upNext ?? lesson?.completionData?.upNext ?? null;
    if (normalizeUpNext(existing)) return;

    // `/v1/up-next` is a dashboard-level recommendation. It can point to a
    // certification/proctored assessment, which is not safe to label as the
    // "next lesson" after an ordinary course activity. Only show a next card
    // when the completion response itself supplies `upnext`.
    return;
  } catch (err) {
    captureSagaError(err, { action: 'completionMounted' });
    // swallow; CompletionScreen falls back to course image / Back-to-course
  }
}

export function* handleCompletionProgressRefresh() {
  const lesson = yield select((s) => s.lesson ?? {});
  const { courseId, status, completionData } = lesson;
  if (status !== 'complete') return;
  const completionModel = completionData?.completionModel;
  const refreshCertificates = Boolean(completionModel?.certificate)
    || completionModel?.mode === 'certificate';
  if (courseId) {
    yield put(coursesRequested());
    yield put(courseDetailRequested(courseId));
  }

  // Practice and summative results can both enable or disable Progress and
  // report access. Refresh at every completion, including consecutive upNext
  // activities. A fresh request supersedes any pre-completion MyStep request.
  yield put(featureVisibilityRequested());

  if (refreshCertificates) {
    // Mirror the targeted Billing refresh used after Library purchases. The
    // lesson completion response is the invalidation signal; Account does not
    // need to refetch its other cached sections.
    yield put(certificatesRequested());
  }
}

export function* handleCompletionContinue() {
  const state = yield select((s) => s.lesson);
  const isFreeDailyStep = state.completionData?.completionModel?.attemptName === 'freedailystep';
  const continueState = getCompletionContinueState({
    model: state.completionData?.completionModel,
    upNext: state.upNext,
  });
  const upNext = continueState.next;

  if (!upNext?.element_id) {
    history.push(isFreeDailyStep ? '/courses' : `/courses/${state.courseId}`);
    return;
  }

  if (!continueState.canContinue) {
    if (continueState.reason === 'suppressed') {
      history.push(`/courses/${state.courseId}`);
      return;
    }
    if (continueState.lockedMessage) {
      yield put(toastShown({ variant: 'warning', message: continueState.lockedMessage }));
    }
    return;
  }

  const courseId = upNext.course_id ?? state.courseId;
  const nextPath = `/learn/${courseId}/${upNext.element_id}${history.location.search || ''}`;
  if (history.location.state) {
    history.replace(nextPath, history.location.state);
  } else {
    history.replace(nextPath);
  }
  yield put(lessonStartRequested({
    courseId,
    elementId: upNext.element_id,
    elementType: upNext.element_type,
    needsInstructionPreflight: true,
  }));
}

export default function* lessonSaga() {
  yield takeLatest(lessonInstructionsRequested.type, handleLessonInstructions);
  yield takeLatest(lessonStartRequested.type, handleLessonStart);
  yield takeLatest(preStartMicVerified.type, handlePreStartMicVerified);
  yield takeLatest(micCheckPassed.type, handleMicCheckPassed);
  yield takeLeading(lessonAnswerSubmitting.type, handleLessonSubmit);
  yield takeLatest(reportQuestionRequested.type, handleReportQuestion);
  yield takeLatest(lessonVideoCheckRequested.type, handleLessonVideoCheck);
  yield takeLatest(lessonCompleted.type, handleCompletionMounted);
  yield takeEvery(
    [
      lessonCompleted.type,
      lessonFeedbackDismissed.type,
    ],
    handleCompletionProgressRefresh,
  );
  yield takeLatest(lessonCompletionContinueRequested.type, handleCompletionContinue);
  yield takeEvery(
    [
      lessonCleared.type,
      lessonNextQuestion.type,
      lessonFeedbackReady.type,
      lessonCompleted.type,
    ],
    handleLessonClearedSideEffect,
  );
}
